import { Button, Typography } from '@mui/material';
import { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { getTranslatedValue } from 'src/common/utils/format-utils';
import { Page } from 'src/components/page';
import { useDialog, useResources } from '../../../hooks';
import { deleteOrganization, getOrganizations } from '../../../thunks/organization-thunks';
import { DataTable, DataTableFilter } from '../../data-table';
import { DataTableFooter } from '../../data-table/data-table-footer';
import { ConfirmDialog } from '../../dialogs';
import { PlusIcon } from '../../icons';
import { OrganizationsEditDialog } from '../organizations-edit-dialog';
import { OrganizationsTableActions } from '../organizations-table-actions';

export const OrganizationsList: ComponentType = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [openOrganizationsEdit, closeOrganizationsEdit] = useDialog(OrganizationsEditDialog);
  const [openConfirm, closeConfirm] = useDialog(ConfirmDialog);
  const {
    list: { hits, isLoading, total },
    query,
    handleQueryUpdate,
    handleFetchFirstPage,
    handleFetchCurrentPage,
  } = useResources({
    entity: 'organization',
    onGetAll: getOrganizations,
  });

  const handleCreate = () => {
    openOrganizationsEdit({
      title: t('organizations.add', 'Добавить организацию'),
      onSubmit: () => {
        closeOrganizationsEdit();
        handleFetchFirstPage();
      },
    });
  };

  const handleEdit = (id: string | number) => {
    openOrganizationsEdit({
      title: t('organizations.edit', 'Редактировать организацию'),
      id,
      onSubmit: () => {
        closeOrganizationsEdit();
        handleFetchCurrentPage();
      },
    });
  };

  const handleDelete = (id: string | number) => {
    openConfirm({
      size: 'small',
      resolve: () => {
        closeConfirm();
        deleteOrganization(id)(dispatch).then(() => {
          handleFetchFirstPage();
        });
      },
    });
  };

  const data = {
    hits,
    isLoading,
    columns: [
      {
        width: 120,
        label: '№',
        labelKey: '',
        renderItem: (item: Record<string, any>) => (
          <Typography variant="h5" color="primary">
            {item.id}
          </Typography>
        ),
      },
      {
        width: 'auto',
        label: 'Название',
        labelKey: 'organizations.name',
        renderItem: (item: Record<string, any>) => getTranslatedValue(item.title, i18n.language),
      },
      {
        width: 'auto',
        label: 'ИНН',
        labelKey: 'organizations.stir',
        renderItem: (item: Record<string, any>) => item.stir,
      },
      {
        width: 96,
        label: '',
        labelKey: '',
        renderItem: (item: Record<string, any>) => (
          <OrganizationsTableActions item={item} onEdit={handleEdit} onDelete={handleDelete} />
        ),
      },
    ],
  };

  return (
    <Page
      title={t('organizations', 'Организации')}
      topButton={
        <Button
          onClick={handleCreate}
          variant="contained"
          color="secondary"
          startIcon={<PlusIcon />}
          size="small"
        >
          {t('organizations.add', 'Добавить организацию')}
        </Button>
      }
    >
      <DataTableFilter {...{ value: query.search, onUpdate: handleQueryUpdate }} />
      <DataTable {...data} />
      <DataTableFooter {...{ total, query, onUpdate: handleQueryUpdate }} />
    </Page>
  );
};
